<template>
  <div>
    <h3 class="heading-3 mb-16 mt-24 md:mt-56">
      {{ i18n(translations.title) }}
    </h3>

    <RevCard class="mb-56">
      <div class="flex">
        <RevInfoBlock
          v-if="errorMessage"
          ref="errorMessageBlock"
          class="mx-20 mb-20 mt-24"
          dismissable
          :icon="IconHeartExploded"
          :title="errorMessage?.title"
          variant="warning"
        >
          <div class="body-2 mt-8">
            <p>{{ errorMessage?.description }}</p>
          </div>
        </RevInfoBlock>
      </div>

      <PaymentForm
        :base-price="cartStore.priceAfterDiscount"
        :create-payment="createPayment"
        :payment-methods="paymentMethods ?? []"
        :resume-payment-id="resumePaymentId"
        @setup-error="handleSetupError"
        @submit-error="handleSubmitError"
        @submit-start="handleSubmitStart"
        @submit-success="handleSubmitSuccess"
        @update:selected-method="handleMethodChange"
      />
    </RevCard>

    <DiscountCard class="mb-56" />

    <ReassuranceItems />
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, ref } from 'vue'

import { buybackCustomerAPI, paymentAPI } from '@backmarket/http-api'
import type {
  PaymentCreateBody,
  PaymentMethod,
} from '@backmarket/http-api/src/api-specs-payment/payment'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevCard } from '@ds/components/Card'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconHeartExploded } from '@ds/icons/IconHeartExploded'

import {
  PaymentForm,
  type PaymentReadableMessage,
  type SetupErrorEvent,
  type SubmitErrorEvent,
  type SubmitStartEvent,
  type SubmitSuccessData,
  useMarketPaymentMethodsFor,
  usePaymentRedirection,
} from '~/scopes/payment'
import ReassuranceItems from '~/scopes/reassurance/components/ReassuranceItems/ReassuranceItems.vue'

import DiscountCard from '../../components/Discount/DiscountCard.vue'
import { useCartStore } from '../../stores/cartStore'
import { useDiscountStore } from '../../stores/discountStore'
import { useLoaderStore } from '../../stores/loaderStore'
import { useSwapStore } from '../../stores/swapStore'

import translations from './Payment.translations'

const experiments = useExperiments()

const i18n = useI18n()
const cartStore = useCartStore()
const discountStore = useDiscountStore()
const loaderStore = useLoaderStore()
const route = useRoute()
const proceedRedirection = usePaymentRedirection()
const swapStore = useSwapStore()
const tracking = useTracking()

const errorMessage = ref<PaymentReadableMessage | null>(null)
const errorMessageBlock = ref()

const { data: paymentMethods } = await useMarketPaymentMethodsFor({
  listings: Object.keys(cartStore.availableItemsById),
  bagPrice: () => cartStore.priceAfterDiscount,
  hasInsurance: cartStore.hasInsurance,
})

const resumePaymentId = computed(() => route.query.paymentId as string)

const createPayment = async (body: PaymentCreateBody) => {
  const paymentResponse = await $httpFetch(paymentAPI.postCreate, {
    body: {
      ...body,
      ...(isEmpty(discountStore.paymentData)
        ? {}
        : {
            promotionCodePk: discountStore.paymentData.id,
            amountMromotion: parseFloat(
              discountStore.paymentData.totalDeduction.amount,
            ),
            slider:
              parseFloat(discountStore.paymentData.totalDeduction.amount) -
              parseFloat(discountStore.paymentData.deduction.amount),
            orderSlider: 'GO',
          }),
      ...(experiments['experiment.serviceFeePriceGroup24'] === 'newPriceGrid24'
        ? { newPriceGrid24: true }
        : {}),
    },
  })

  if (swapStore.hasOffer) {
    await $httpFetch(buybackCustomerAPI.postOrder, {
      body: {
        paymentId: paymentResponse.paymentId,
        listingId: swapStore.offer?.sourcing_listing,
      },
    })
  }

  return paymentResponse
}

const handleMethodChange = async (
  method: PaymentMethod | null,
  isSelectedByUser: boolean,
) => {
  errorMessage.value = null

  if (isSelectedByUser) {
    tracking.trackClick({
      name: `4-Payment_list-${method?.bmCode}`,
      zone: 'funnel',
    })
  }

  try {
    if (method) {
      await discountStore.setPaymentMethod(method)
    }
  } catch {
    // Fails silently
  }
}

const handleSubmitStart = ({
  customLoadingMessage,
  paymentMethod,
}: SubmitStartEvent) => {
  errorMessage.value = null
  loaderStore.enable({
    message: customLoadingMessage || i18n(translations.loadingPayment),
  })
  tracking.trackClick({
    value: paymentMethod?.bmCode || '',
    name: '4-Payment_cta-pay',
    zone: 'funnel',
  })
}

const showErrorMessage = async (
  message: PaymentReadableMessage | undefined,
) => {
  if (message) {
    errorMessage.value = message
    errorMessageBlock.value?.scrollIntoView({ behavior: 'smooth' })
  }
}

const handleSetupError = async ({ message }: SetupErrorEvent) => {
  await showErrorMessage(message)
}

const handleSubmitSuccess = ({ redirection }: SubmitSuccessData) => {
  cartStore.setShowCatchupModal(true)

  proceedRedirection(redirection)
}

const handleSubmitError = async ({
  redirection,
  message,
}: SubmitErrorEvent) => {
  if (redirection) {
    proceedRedirection(redirection)

    return
  }

  loaderStore.disable()
  await showErrorMessage(message)
}
</script>
